import { useState } from 'react'
import {
  useContactSubmissions,
  useUpdateContactStatus,
  useDeleteContactSubmission,
} from '../hooks/useContacts'
import { Button } from '../components/ui/Button'
import { Alert } from '../components/ui/Alert'
import { Spinner } from '../components/ui/Spinner'
import { Modal } from '../components/ui/Modal'
import { Mail, MailOpen, MessageSquare, Trash2, Download, Eye } from 'lucide-react'
import type { ContactSubmission } from '../types'
import { exportToPDF } from '../utils/pdf'

const STATUS_LABELS = {
  unread: 'Unread',
  read: 'Read',
  responded: 'Responded',
  all: 'All',
}

const STATUS_ICONS = {
  unread: <Mail size={16} className="text-rhs-maroon" />,
  read: <MailOpen size={16} className="text-gray-600" />,
  responded: <MessageSquare size={16} className="text-rhs-gold" />,
}

export function ContactPage() {
  const [statusFilter, setStatusFilter] = useState<'unread' | 'read' | 'responded' | 'all'>(
    'unread'
  )
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null)

  const { data, isLoading, error } = useContactSubmissions(statusFilter)
  const updateStatusMutation = useUpdateContactStatus()
  const deleteMutation = useDeleteContactSubmission()

  const handleStatusChange = async (id: string, newStatus: 'unread' | 'read' | 'responded') => {
    try {
      await updateStatusMutation.mutateAsync({ id, status: newStatus })
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const toggleReadStatus = async (id: string, currentStatus: 'unread' | 'read' | 'responded') => {
    // Toggle between unread and read only
    const nextStatus = currentStatus === 'unread' ? 'read' : 'unread'
    await handleStatusChange(id, nextStatus)
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete submission from ${name}?`)) return

    try {
      await deleteMutation.mutateAsync(id)
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(null)
      }
    } catch (error) {
      console.error('Failed to delete:', error)
    }
  }

  const handleExportPDF = () => {
    if (!data?.submissions) return
    exportToPDF(data.submissions, statusFilter)
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (isLoading) return <Spinner />
  if (error) return <Alert variant="error">{(error as Error).message}</Alert>

  const submissions = data?.submissions || []

  return (
    <>
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-coolgray shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading text-rhs-maroon truncate">
                Contact Inbox
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {data?.total || 0} {statusFilter === 'all' ? 'total' : statusFilter}
              </p>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 overflow-x-auto">
              {(['unread', 'read', 'responded', 'all'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-sm font-title rounded-md whitespace-nowrap transition-colors ${
                    statusFilter === status
                      ? 'bg-rhs-maroon text-white'
                      : 'bg-ice hover:bg-ice-dark text-rhs-black'
                  }`}
                >
                  {STATUS_LABELS[status]}
                </button>
              ))}
            </div>

            {/* Export Button */}
            {submissions.length > 0 && (
              <Button
                onClick={handleExportPDF}
                variant="secondary"
                className="flex items-center gap-2 flex-shrink-0"
              >
                <Download size={18} />
                <span className="hidden sm:inline">Export PDF</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Empty State */}
        {submissions.length === 0 ? (
          <div className="bg-ice-dark rounded-card p-8 sm:p-12 text-center border border-coolgray">
            <p className="text-gray-600 text-base sm:text-lg font-title">
              No {statusFilter === 'all' ? '' : statusFilter} submissions
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white border border-coolgray rounded-lg p-3 hover:shadow-gold transition-shadow"
              >
                <div className="flex items-start gap-2">
                  {/* Clickable Status Badge */}
                  <div className="flex-shrink-0 flex flex-col gap-1">
                    <button
                      onClick={() => toggleReadStatus(submission.id, submission.status)}
                      disabled={updateStatusMutation.isPending}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-title font-semibold transition-colors hover:bg-ice-dark disabled:opacity-50"
                      title={`Click to mark as ${submission.status === 'unread' ? 'read' : 'unread'}`}
                    >
                      {STATUS_ICONS[submission.status]}
                      <span className="capitalize">{submission.status}</span>
                    </button>

                    {/* Responded Toggle */}
                    {submission.status !== 'responded' ? (
                      <button
                        onClick={() => handleStatusChange(submission.id, 'responded')}
                        disabled={updateStatusMutation.isPending}
                        className="p-1 text-gray-600 hover:text-rhs-gold hover:bg-ice rounded transition-colors disabled:opacity-50 self-start"
                        title="Mark as responded"
                        aria-label="Mark as responded"
                      >
                        <MessageSquare size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(submission.id, 'read')}
                        disabled={updateStatusMutation.isPending}
                        className="p-1 text-rhs-gold hover:text-gray-600 hover:bg-ice rounded transition-colors disabled:opacity-50 self-start"
                        title="Clear responded status"
                        aria-label="Clear responded status"
                      >
                        <MessageSquare size={16} className="fill-current" />
                      </button>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-title font-semibold text-rhs-black text-sm truncate">
                          {submission.name}
                        </h3>
                        <p className="text-xs text-gray-600 truncate">
                          {submission.email}
                          {submission.phone && ` • ${submission.phone}`}
                        </p>
                      </div>
                      <span className="text-xs text-gray-600 whitespace-nowrap ml-2">
                        {formatDate(submission.created_at)}
                      </span>
                    </div>

                    <p className="text-sm text-rhs-black line-clamp-2 mb-2">{submission.message}</p>

                    {/* Icon Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="p-1.5 text-gray-600 hover:text-rhs-maroon hover:bg-ice rounded transition-colors"
                        title="View full message"
                        aria-label="View full message"
                      >
                        <Eye size={16} />
                      </button>

                      <button
                        onClick={() => handleDelete(submission.id, submission.name)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-ice rounded transition-colors disabled:opacity-50"
                        title="Delete submission"
                        aria-label="Delete submission"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full Message Modal */}
      {selectedSubmission && (
        <Modal isOpen={true} onClose={() => setSelectedSubmission(null)} title="Contact Submission">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-title font-semibold text-gray-600 uppercase">
                Name
              </label>
              <p className="text-rhs-black font-title">{selectedSubmission.name}</p>
            </div>

            <div>
              <label className="text-xs font-title font-semibold text-gray-600 uppercase">
                Email
              </label>
              <p className="text-rhs-black">
                <a
                  href={`mailto:${selectedSubmission.email}`}
                  className="text-rhs-maroon hover:underline"
                >
                  {selectedSubmission.email}
                </a>
              </p>
            </div>

            {selectedSubmission.phone && (
              <div>
                <label className="text-xs font-title font-semibold text-gray-600 uppercase">
                  Phone
                </label>
                <p className="text-rhs-black">
                  <a
                    href={`tel:${selectedSubmission.phone}`}
                    className="text-rhs-maroon hover:underline"
                  >
                    {selectedSubmission.phone}
                  </a>
                </p>
              </div>
            )}

            <div>
              <label className="text-xs font-title font-semibold text-gray-600 uppercase">
                Message
              </label>
              <p className="text-rhs-black whitespace-pre-wrap">{selectedSubmission.message}</p>
            </div>

            <div>
              <label className="text-xs font-title font-semibold text-gray-600 uppercase">
                Status
              </label>
              <p className="text-rhs-black capitalize">{selectedSubmission.status}</p>
            </div>

            <div>
              <label className="text-xs font-title font-semibold text-gray-600 uppercase">
                Submitted
              </label>
              <p className="text-rhs-black">{formatDate(selectedSubmission.created_at)}</p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setSelectedSubmission(null)} className="flex-1">
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
